import { useMemo } from "react";

import { TD, TH } from "./table";

import { PreclassCheckMessages } from "components/local_modal/preclass_check/preclass_check_messages";
import { useArraySelector } from "hooks/redux_hooks";
import { useUserProfileProperties } from "hooks/use_user_properties";
import {
  MonitoringEventTypeType,
  PreClassCheckResultMonitoringEventType,
} from "shared/types/monitoring_event";
import { PreclassTestOutcomeType, PreclassTestResultType } from "shared/types/preclass_check";
import { selectMonitoringEventsByType } from "store/selectors/monitoring";
import { formatDateTime } from "utils/date";
import { longDisplayName } from "utils/user";

const PRECLASS_CHECK_MESSAGES = new PreclassCheckMessages();

function PreclassCheckFailureHeader() {
  return (
    <thead>
      <tr>
        <TH>User ID</TH>
        <TH>User Name</TH>
        <TH>Role</TH>
        <TH>Type</TH>
        <TH>Message</TH>
        <TH>Date</TH>
      </tr>
    </thead>
  );
}

type FailedTestType = {
  id: string;
  userId: string;
  timestamp: number;
} & PreclassTestResultType;

function PreclassCheckFailureBody() {
  const events = useArraySelector(
    selectMonitoringEventsByType(MonitoringEventTypeType.PRECLASS_CHECK_FAIL),
    ["id"],
  ) as PreClassCheckResultMonitoringEventType[];

  const failedTests = useMemo(() => {
    const output: FailedTestType[] = [];
    events.forEach((event) => {
      const tests = Object.values(event.contextData).filter(({ outcome }) =>
        [
          PreclassTestOutcomeType.FAIL,
          PreclassTestOutcomeType.ABORT,
          PreclassTestOutcomeType.EXCEPTION,
        ].includes(outcome),
      );
      tests.forEach((test) => {
        output.push({ ...event, ...test });
      });
    });
    return output;
  }, [events]);

  return (
    <tbody>
      {failedTests.map((test) => {
        return <PreclassCheckFailureRow key={`${test.id}-${test.name}`} test={test} />;
      })}
    </tbody>
  );
}

function PreclassCheckFailureRow({ test }: { test: FailedTestType }) {
  const user = useUserProfileProperties(test.userId);
  return (
    <tr>
      <TD>{test.userId}</TD>
      <TD>{longDisplayName(user) || "(not enrolled in class)"}</TD>
      <TD>{user?.role || "-"}</TD>
      <TD>{`${test.category} / ${test.name}`}</TD>
      <TD>{(test.error && PRECLASS_CHECK_MESSAGES.errorInfoMap.get(test.error)?.message) || ""}</TD>
      <TD noWrap>{formatDateTime(test.timestamp)}</TD>
    </tr>
  );
}

function PreclassCheckFailureTable() {
  return (
    <table
      className="preclass-check-failures fds-table table-hover"
      style={{ tableLayout: "auto" }}
      data-testid="preclass-check-failure-table"
    >
      <PreclassCheckFailureHeader />
      <PreclassCheckFailureBody />
    </table>
  );
}

export {
  PreclassCheckFailureHeader,
  PreclassCheckFailureBody,
  PreclassCheckFailureRow,
  PreclassCheckFailureTable,
};
